import {
  type AssetListItem,
  type AssetListQuery,
  type AssetListSortBy,
} from '@gb-ticket/shared-types';
import { type ColumnDef, flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { ArrowDownIcon, ArrowUpIcon, ArrowUpDownIcon } from 'lucide-react';
import { useMemo } from 'react';
import { Link } from 'react-router';

import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  AssetStatusBadge,
  DateExpiryBadge,
} from '@/products/helpdesk/features/assets/asset-badges';
import { columnAriaSort } from '@/lib/a11y';
import { formatDate } from '@/lib/format';
import { PersonChip } from '@/platform/components/person-chip';

declare module '@tanstack/react-table' {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- interface params required by the library
  interface ColumnMeta<TData, TValue> {
    className?: string;
  }
}

type AssetsTableProps = {
  items: AssetListItem[];
  query: AssetListQuery;
  onSortChange: (sortBy: AssetListSortBy) => void;
};

const SORTABLE = new Set<string>(['name', 'inventoryTag', 'status', 'warrantyUntil', 'updatedAt']);

export function AssetsTable({ items, query, onSortChange }: AssetsTableProps) {
  const columns = useMemo<ColumnDef<AssetListItem>[]>(
    () => [
      {
        accessorKey: 'inventoryTag',
        header: 'Inventar',
        cell: ({ row }) => row.original.inventoryTag ?? '—',
      },
      {
        accessorKey: 'name',
        header: 'Bezeichnung',
        cell: ({ row }) => (
          <div>
            <div className="font-medium">{row.original.name}</div>
            <div className="text-xs text-muted-foreground">{row.original.assetType.name}</div>
          </div>
        ),
      },
      {
        accessorKey: 'status',
        header: 'Status',
        cell: ({ row }) => <AssetStatusBadge status={row.original.status} />,
      },
      {
        id: 'assignedUser',
        header: 'Nutzer',
        meta: { className: 'hidden md:table-cell' },
        cell: ({ row }) =>
          row.original.assignedUser ? (
            <PersonChip
              displayName={row.original.assignedUser.displayName}
              profilePhotoUrl={row.original.assignedUser.profilePhotoUrl}
            />
          ) : (
            '—'
          ),
      },
      {
        id: 'location',
        header: 'Standort',
        meta: { className: 'hidden lg:table-cell' },
        cell: ({ row }) => row.original.location ?? row.original.department?.name ?? '—',
      },
      {
        accessorKey: 'warrantyUntil',
        header: 'Garantie',
        meta: { className: 'hidden xl:table-cell' },
        cell: ({ row }) => (
          <DateExpiryBadge
            expired={row.original.warrantyExpired}
            label={formatDate(row.original.warrantyUntil)}
          />
        ),
      },
      {
        id: 'actions',
        header: '',
        cell: ({ row }) => (
          <Button asChild variant="outline" size="sm">
            <Link to={`/helpdesk/assets/${row.original.id}`}>Ansehen</Link>
          </Button>
        ),
      },
    ],
    [],
  );

  const table = useReactTable({
    data: items,
    columns,
    getCoreRowModel: getCoreRowModel(),
    manualSorting: true,
    getRowId: (row) => row.id,
  });

  return (
    <Table>
      <TableHeader>
        {table.getHeaderGroups().map((headerGroup) => (
          <TableRow key={headerGroup.id}>
            {headerGroup.headers.map((header) => {
              const sortKey = header.column.id;
              const canSort = SORTABLE.has(sortKey);
              return (
                <TableHead
                  key={header.id}
                  className={header.column.columnDef.meta?.className}
                  aria-sort={
                    canSort ? columnAriaSort(sortKey, query.sortBy, query.sortOrder) : undefined
                  }
                >
                  {canSort ? (
                    <button
                      type="button"
                      className="inline-flex items-center gap-1 hover:text-foreground"
                      onClick={() => {
                        if (isSortBy(sortKey)) {
                          onSortChange(sortKey);
                        }
                      }}
                    >
                      {flexRender(header.column.columnDef.header, header.getContext())}
                      <SortIcon active={query.sortBy === sortKey} order={query.sortOrder} />
                    </button>
                  ) : (
                    flexRender(header.column.columnDef.header, header.getContext())
                  )}
                </TableHead>
              );
            })}
          </TableRow>
        ))}
      </TableHeader>
      <TableBody>
        {table.getRowModel().rows.length === 0 ? (
          <TableRow>
            <TableCell colSpan={columns.length} className="h-24 text-center text-muted-foreground">
              Keine Assets gefunden.
            </TableCell>
          </TableRow>
        ) : (
          table.getRowModel().rows.map((row) => (
            <TableRow key={row.id}>
              {row.getVisibleCells().map((cell) => (
                <TableCell key={cell.id} className={cell.column.columnDef.meta?.className}>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </TableCell>
              ))}
            </TableRow>
          ))
        )}
      </TableBody>
    </Table>
  );
}

function isSortBy(value: string): value is AssetListSortBy {
  return SORTABLE.has(value);
}

function SortIcon({ active, order }: { active: boolean; order: 'asc' | 'desc' }) {
  if (!active) {
    return <ArrowUpDownIcon className="size-3.5 text-muted-foreground" aria-hidden />;
  }
  return order === 'asc' ? (
    <ArrowUpIcon className="size-3.5" aria-hidden />
  ) : (
    <ArrowDownIcon className="size-3.5" aria-hidden />
  );
}
